import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Save, ArrowLeft, Upload, Plus, Trash2, Image as ImageIcon, Sparkles } from "lucide-react";

import DashboardLayout from "@/components/DashboardLayout";
import { api, apiUrl, extractErrorMessage } from "@/lib/api";
import { FORMATS, GENRES, STAGES, COUNTRIES, INDUSTRIES, VALUES_LIST, PLACEMENT_TYPES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const empty = {
  title: "",
  cover_url: "",
  logline: "",
  synopsis_short: "",
  synopsis_long: "",
  format: "largometraje",
  genre: "drama",
  duration_min: null,
  origin_country: "Chile",
  original_language: "Español",
  languages: [],
  exploitation_territories: [],
  stage: "development",
  themes: [],
  values: [],
  tone: "",
  main_characters: "",
  audience_primary: "",
  audience_age_min: null,
  audience_age_max: null,
  audience_countries: [],
  audience_interests: [],
  audience_platforms: [],
  total_budget: null,
  currency: "USD",
  confirmed_financing: 0,
  requested_on_cinemage: null,
  financing_percent: null,
  compatible_sectors: [],
  excluded_categories: [],
  seeks_placement: true,
  seeks_sponsorship: true,
  seeks_cultural_funds: false,
  placements: [],
  packages: [],
  cultural_funds: [],
  reel_url: "",
  privacy: "public",
  published: false,
};

export default function ProjectEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const { t } = useTranslation();
  const [proj, setProj] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/api/projects/${id}`);
        setProj({ ...empty, ...data });
      } catch (e) {
        toast.error(t("editor_project.toast_load_failed"));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  const set = (patch) => setProj((p) => ({ ...p, ...patch }));

  const financingPct = useMemo(() => {
    if (!proj.total_budget || proj.total_budget <= 0) return proj.financing_percent || 0;
    return Math.min(100, Math.round(((proj.confirmed_financing || 0) / proj.total_budget) * 100));
  }, [proj.total_budget, proj.confirmed_financing, proj.financing_percent]);

  const save = async ({ publish = false } = {}) => {
    setSaving(true);
    try {
      const payload = { ...proj, financing_percent: financingPct };
      if (!payload.title.trim()) {
        toast.error(t("editor_project.title_required"));
        setSaving(false);
        return;
      }
      let saved;
      const wasFirstPublish = publish && !proj.published;
      if (id) {
        const { data } = await api.put(`/api/projects/${id}`, payload);
        saved = data;
      } else {
        const { data } = await api.post("/api/projects", payload);
        saved = data;
      }
      if (publish && saved.id) {
        await api.post(`/api/projects/${saved.id}/publish`);
      }
      toast.success(publish ? t("editor_project.toast_published") : t("editor_project.toast_saved"));
      if (wasFirstPublish && saved.id) {
        // Social proof preview: show top 3 matching sponsors
        nav(`/dashboard/proyectos/${saved.id}/prueba-social`, { replace: true });
      } else {
        nav("/dashboard/proyectos", { replace: true });
      }
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const uploadCover = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set({ cover_url: apiUrl(data.url) });
      toast.success(t("editor_project.cover_uploaded"));
    } catch (e) {
      toast.error(t("editor_project.cover_upload_failed"));
    }
  };

  if (loading) {
    return (
      <DashboardLayout title={t("editor_project.title_edit")}>
        <div className="text-slate-500">{t("common.loading")}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title={id ? t("editor_project.title_edit") : t("editor_project.title_new")}
      subtitle={t("editor_project.subtitle")}
      actions={
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => nav("/dashboard/proyectos")} className="rounded-full">
            <ArrowLeft size={16} className="mr-2" /> {t("editor_project.back")}
          </Button>
          {id && (
            <Button
              onClick={() => nav(`/dashboard/proyectos/${id}/analisis-ia`)}
              variant="outline"
              data-testid="button-open-script-analysis"
              className="rounded-full border-gold/40 text-petroleum hover:bg-gold/10"
            >
              <Sparkles size={16} className="mr-2 text-gold" /> {t("editor_project.ai_analysis")}
            </Button>
          )}
          <Button onClick={() => save({ publish: false })} disabled={saving} data-testid="button-save-project" className="bg-slate-100 text-petroleum hover:bg-slate-200 rounded-full">
            <Save size={16} className="mr-2" /> {saving ? t("editor_project.saving") : t("editor_project.save_draft")}
          </Button>
          <Button onClick={() => save({ publish: true })} disabled={saving} data-testid="button-publish-project" className="bg-petroleum text-white hover:bg-petroleum/90 rounded-full">
            {t("editor_project.publish")}
          </Button>
        </div>
      }
    >
      <Tabs defaultValue="basic">
        <TabsList className="mb-6 bg-slate-100 rounded-full">
          <TabsTrigger value="basic" data-testid="tab-basic">{t("editor_project.tab_basic")}</TabsTrigger>
          <TabsTrigger value="creative" data-testid="tab-creative">{t("editor_project.tab_creative")}</TabsTrigger>
          <TabsTrigger value="audience" data-testid="tab-audience">{t("editor_project.tab_audience")}</TabsTrigger>
          <TabsTrigger value="finance" data-testid="tab-finance">{t("editor_project.tab_finance")}</TabsTrigger>
          <TabsTrigger value="strategy" data-testid="tab-strategy">{t("editor_project.tab_strategy")}</TabsTrigger>
          <TabsTrigger value="media" data-testid="tab-media">{t("editor_project.tab_media")}</TabsTrigger>
        </TabsList>

        {/* Basic */}
        <TabsContent value="basic" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_basic")}</h3>
            <div className="grid md:grid-cols-2 gap-5 mt-4">
              <Field label={t("editor_project.field_title")} required>
                <Input value={proj.title} onChange={(e) => set({ title: e.target.value })} data-testid="input-project-title" />
              </Field>
              <Field label={t("editor_project.field_logline")}>
                <Input value={proj.logline || ""} onChange={(e) => set({ logline: e.target.value })} data-testid="input-project-logline" />
              </Field>
              <Field label={t("editor_project.field_format")}>
                <SelectField value={proj.format} onChange={(v) => set({ format: v })} options={FORMATS} testid="select-project-format" />
              </Field>
              <Field label={t("editor_project.field_genre")}>
                <SelectField value={proj.genre} onChange={(v) => set({ genre: v })} options={GENRES.map((g) => ({ value: g, label: g }))} testid="select-project-genre" />
              </Field>
              <Field label={t("editor_project.field_country")}>
                <SelectField value={proj.origin_country} onChange={(v) => set({ origin_country: v })} options={COUNTRIES.map((c) => ({ value: c, label: c }))} />
              </Field>
              <Field label={t("editor_project.field_language")}>
                <Input value={proj.original_language || ""} onChange={(e) => set({ original_language: e.target.value })} />
              </Field>
              <Field label={t("editor_project.field_duration")}>
                <Input type="number" value={proj.duration_min ?? ""} onChange={(e) => set({ duration_min: e.target.value ? parseInt(e.target.value, 10) : null })} />
              </Field>
              <Field label={t("editor_project.field_stage")}>
                <SelectField value={proj.stage} onChange={(v) => set({ stage: v })} options={STAGES} testid="select-project-stage" />
              </Field>
            </div>
            <Field label={t("editor_project.field_synopsis_short")} className="mt-5">
              <Textarea value={proj.synopsis_short || ""} onChange={(e) => set({ synopsis_short: e.target.value })} rows={3} data-testid="textarea-synopsis-short" />
            </Field>
            <Field label={t("editor_project.field_synopsis_long")} className="mt-5">
              <Textarea value={proj.synopsis_long || ""} onChange={(e) => set({ synopsis_long: e.target.value })} rows={5} />
            </Field>
          </Card>

          <Card>
            <h3 className="section-title">{t("editor_project.section_cover")}</h3>
            <div className="mt-4 flex items-center gap-6">
              <div className="w-48 h-32 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden">
                {proj.cover_url ? <img src={proj.cover_url} alt="cover" className="w-full h-full object-cover" /> : <ImageIcon className="text-slate-400" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadCover(e.target.files?.[0])} />
                <Button variant="outline" onClick={() => fileRef.current?.click()} data-testid="button-upload-cover">
                  <Upload size={14} className="mr-2" /> {t("editor_project.cover_upload")}
                </Button>
                <p className="text-xs text-slate-500 mt-2">{t("editor_project.cover_hint")}</p>
                {proj.cover_url && (
                  <button onClick={() => set({ cover_url: "" })} className="text-xs text-red-600 mt-2">{t("editor_project.cover_remove")}</button>
                )}
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="section-title">{t("editor_project.section_privacy")}</h3>
            <div className="mt-4 flex flex-wrap gap-3">
              {["public", "private", "invite"].map((p) => (
                <button
                  key={p}
                  onClick={() => set({ privacy: p })}
                  data-testid={`button-privacy-${p}`}
                  className={`text-sm px-4 py-2 rounded-full border ${proj.privacy === p ? "bg-petroleum text-white border-petroleum" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"}`}
                >
                  {p === "public" ? t("editor_project.privacy_public") : p === "private" ? t("editor_project.privacy_private") : t("editor_project.privacy_invite")}
                </button>
              ))}
            </div>
          </Card>
        </TabsContent>

        {/* Creative */}
        <TabsContent value="creative" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_creative")}</h3>
            <div className="mt-4 space-y-5">
              <Field label={t("editor_project.field_tone")}>
                <Input value={proj.tone || ""} onChange={(e) => set({ tone: e.target.value })} />
              </Field>
              <Field label={t("editor_project.field_main_characters")}>
                <Textarea value={proj.main_characters || ""} onChange={(e) => set({ main_characters: e.target.value })} rows={3} />
              </Field>
              <ChipsField t={t} label={t("editor_project.field_themes")} values={proj.themes} onChange={(v) => set({ themes: v })} />
              <ChipsField t={t} label={t("editor_project.field_values")} values={proj.values} onChange={(v) => set({ values: v })} suggestions={VALUES_LIST} />
              <ChipsField t={t} label={t("editor_project.field_territories")} values={proj.exploitation_territories} onChange={(v) => set({ exploitation_territories: v })} suggestions={COUNTRIES} />
            </div>
          </Card>
        </TabsContent>

        {/* Audience */}
        <TabsContent value="audience" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_audience")}</h3>
            <div className="grid md:grid-cols-2 gap-5 mt-4">
              <Field label={t("editor_project.field_audience_primary")}>
                <Input value={proj.audience_primary || ""} onChange={(e) => set({ audience_primary: e.target.value })} />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label={t("editor_project.field_audience_age_min")}>
                  <Input type="number" value={proj.audience_age_min ?? ""} onChange={(e) => set({ audience_age_min: e.target.value ? parseInt(e.target.value, 10) : null })} />
                </Field>
                <Field label={t("editor_project.field_audience_age_max")}>
                  <Input type="number" value={proj.audience_age_max ?? ""} onChange={(e) => set({ audience_age_max: e.target.value ? parseInt(e.target.value, 10) : null })} />
                </Field>
              </div>
            </div>
            <div className="mt-5 space-y-5">
              <ChipsField t={t} label={t("editor_project.field_audience_countries")} values={proj.audience_countries} onChange={(v) => set({ audience_countries: v })} suggestions={COUNTRIES} />
              <ChipsField t={t} label={t("editor_project.field_interests")} values={proj.audience_interests} onChange={(v) => set({ audience_interests: v })} />
              <ChipsField t={t} label={t("editor_project.field_platforms")} values={proj.audience_platforms} onChange={(v) => set({ audience_platforms: v })} suggestions={["Netflix", "Prime", "HBO", "Disney+", "Paramount+", "Movistar+", "MUBI", "Festival", "TV abierta", "TikTok", "YouTube", "Instagram", "Cinemas"]} />
            </div>
          </Card>
        </TabsContent>

        {/* Finance */}
        <TabsContent value="finance" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_finance")}</h3>
            <div className="grid md:grid-cols-3 gap-5 mt-4">
              <Field label={t("editor_project.field_total_budget")}>
                <Input type="number" value={proj.total_budget ?? ""} onChange={(e) => set({ total_budget: e.target.value ? parseFloat(e.target.value) : null })} />
              </Field>
              <Field label={t("editor_project.field_currency")}>
                <Select value={proj.currency} onValueChange={(v) => set({ currency: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["USD", "EUR", "CLP", "MXN", "ARS", "COP", "PEN", "BRL"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t("editor_project.field_confirmed_funding")}>
                <Input type="number" value={proj.confirmed_financing ?? ""} onChange={(e) => set({ confirmed_financing: e.target.value ? parseFloat(e.target.value) : 0 })} />
              </Field>
              <Field label={t("editor_project.field_requested")}>
                <Input type="number" value={proj.requested_on_cinemage ?? ""} onChange={(e) => set({ requested_on_cinemage: e.target.value ? parseFloat(e.target.value) : null })} />
              </Field>
              <Field label={t("editor_project.field_financed_pct")}>
                <Input type="text" value={`${financingPct}%`} disabled />
              </Field>
            </div>
          </Card>
        </TabsContent>

        {/* Strategy */}
        <TabsContent value="strategy" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_modalities")}</h3>
            <div className="mt-4 space-y-4">
              <SwitchRow label={t("editor_project.mod_placement")} value={proj.seeks_placement} onChange={(v) => set({ seeks_placement: v })} />
              <SwitchRow label={t("editor_project.mod_sponsorship")} value={proj.seeks_sponsorship} onChange={(v) => set({ seeks_sponsorship: v })} />
              <SwitchRow label={t("editor_project.mod_funds")} value={proj.seeks_cultural_funds} onChange={(v) => set({ seeks_cultural_funds: v })} />
            </div>
          </Card>

          <Card>
            <h3 className="section-title">{t("editor_project.section_brand_compat")}</h3>
            <div className="mt-4 space-y-5">
              <ChipsField t={t} label={t("editor_project.field_compat_sectors")} values={proj.compatible_sectors} onChange={(v) => set({ compatible_sectors: v })} suggestions={INDUSTRIES} />
              <ChipsField t={t} label={t("editor_project.field_excluded_cats")} values={proj.excluded_categories} onChange={(v) => set({ excluded_categories: v })} />
            </div>
          </Card>

          <PlacementsEditor t={t} placements={proj.placements || []} onChange={(v) => set({ placements: v })} />
          <PackagesEditor t={t} packages={proj.packages || []} onChange={(v) => set({ packages: v })} />
        </TabsContent>

        {/* Media */}
        <TabsContent value="media" className="space-y-6">
          <Card>
            <h3 className="section-title">{t("editor_project.section_reel")}</h3>
            <div className="mt-4 space-y-5">
              <Field label={t("editor_project.field_reel")}>
                <Input value={proj.reel_url || ""} onChange={(e) => set({ reel_url: e.target.value })} placeholder="https://vimeo.com/…" data-testid="input-reel-url" />
              </Field>
              <ScriptUploader t={t} project={proj} onChange={(patch) => set(patch)} />
            </div>
          </Card>
        </TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}

function Card({ children }) {
  return <div className="kpi-card p-6">{children}</div>;
}

function Field({ label, children, required, className }) {
  return (
    <div className={className}>
      <Label className="text-sm text-slate-600">
        {label}{required && <span className="text-red-500 ml-1">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SelectField({ value, onChange, options, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function SwitchRow({ label, value, onChange }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-100 pb-3 last:border-0">
      <span className="text-sm text-slate-700">{label}</span>
      <Switch checked={!!value} onCheckedChange={onChange} data-testid={`switch-${label.toLowerCase().replace(/\s/g, "-")}`} />
    </div>
  );
}

function ChipsField({ t, label, values, onChange, suggestions }) {
  const [input, setInput] = useState("");
  const add = (v) => {
    const clean = (v || "").trim();
    if (!clean) return;
    if ((values || []).includes(clean)) return;
    onChange([...(values || []), clean]);
    setInput("");
  };
  return (
    <div>
      <Label className="text-sm text-slate-600">{label}</Label>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {(values || []).map((v) => (
          <span key={v} className="text-sm inline-flex items-center gap-1 bg-slate-100 text-slate-700 rounded-full px-3 py-1">
            {v}
            <button onClick={() => onChange(values.filter((x) => x !== v))} className="text-slate-500 hover:text-red-600">×</button>
          </span>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add(input))} placeholder={t("editor_project.chips_add_placeholder")} />
        <Button type="button" variant="outline" onClick={() => add(input)}>{t("editor_project.chips_add")}</Button>
      </div>
      {suggestions?.length ? (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {suggestions.filter((s) => !(values || []).includes(s)).slice(0, 12).map((s) => (
            <button key={s} type="button" onClick={() => add(s)} className="text-xs text-slate-500 hover:text-petroleum border border-slate-200 rounded-full px-2 py-0.5">+ {s}</button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function PlacementsEditor({ t, placements, onChange }) {
  const add = () => onChange([
    ...placements,
    { id: cryptoId(), name: "", placement_type: "narrative", requested_value: null, currency: "USD", description: "", status: "available" },
  ]);
  const update = (i, patch) => onChange(placements.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const remove = (i) => onChange(placements.filter((_, idx) => idx !== i));
  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="section-title">{t("editor_project.placements_title")}</h3>
        <Button variant="outline" onClick={add} data-testid="button-add-placement"><Plus size={14} className="mr-1" /> {t("editor_project.add")}</Button>
      </div>
      <div className="mt-4 space-y-4">
        {placements.length === 0 && <p className="text-sm text-slate-500">{t("editor_project.placements_empty")}</p>}
        {placements.map((p, i) => (
          <div key={p.id || i} className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-petroleum">{t("editor_project.opportunity_n", { n: i + 1 })}</h4>
              <button onClick={() => remove(i)} className="text-red-600 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> {t("editor_project.remove")}</button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 mt-3">
              <Field label={t("editor_project.field_opportunity_name")}><Input value={p.name} onChange={(e) => update(i, { name: e.target.value })} /></Field>
              <Field label={t("editor_project.field_opportunity_type")}>
                <SelectField value={p.placement_type} onChange={(v) => update(i, { placement_type: v })} options={PLACEMENT_TYPES} />
              </Field>
              <Field label={t("editor_project.field_product_category")}><Input value={p.product_category || ""} onChange={(e) => update(i, { product_category: e.target.value })} /></Field>
              <Field label={t("editor_project.field_requested_value")}><Input type="number" value={p.requested_value ?? ""} onChange={(e) => update(i, { requested_value: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
            </div>
            <Field label={t("editor_project.field_description")} className="mt-3">
              <Textarea rows={2} value={p.description || ""} onChange={(e) => update(i, { description: e.target.value })} />
            </Field>
          </div>
        ))}
      </div>
    </Card>
  );
}

function PackagesEditor({ t, packages, onChange }) {
  const add = () => onChange([
    ...packages,
    { id: cryptoId(), name: "", tier: "official", requested_value: null, currency: "USD", benefits: [] },
  ]);
  const update = (i, patch) => onChange(packages.map((p, idx) => idx === i ? { ...p, ...patch } : p));
  const remove = (i) => onChange(packages.filter((_, idx) => idx !== i));
  return (
    <Card>
      <div className="flex items-center justify-between">
        <h3 className="section-title">{t("editor_project.packages_title")}</h3>
        <Button variant="outline" onClick={add} data-testid="button-add-package"><Plus size={14} className="mr-1" /> {t("editor_project.add")}</Button>
      </div>
      <div className="mt-4 space-y-4">
        {packages.length === 0 && <p className="text-sm text-slate-500">{t("editor_project.packages_empty")}</p>}
        {packages.map((p, i) => (
          <div key={p.id || i} className="border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-petroleum">{t("editor_project.package_n", { n: i + 1 })}</h4>
              <button onClick={() => remove(i)} className="text-red-600 text-xs inline-flex items-center gap-1"><Trash2 size={12} /> {t("editor_project.remove")}</button>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mt-3">
              <Field label={t("editor_project.field_package_name")}><Input value={p.name} onChange={(e) => update(i, { name: e.target.value })} /></Field>
              <Field label={t("editor_project.field_tier")}>
                <SelectField value={p.tier} onChange={(v) => update(i, { tier: v })} options={[
                  { value: "main", label: t("editor_project.tier_main") },
                  { value: "official", label: t("editor_project.tier_official") },
                  { value: "associated", label: t("editor_project.tier_associated") },
                  { value: "collaborator", label: t("editor_project.tier_collaborator") },
                ]} />
              </Field>
              <Field label={t("editor_project.field_requested_value")}><Input type="number" value={p.requested_value ?? ""} onChange={(e) => update(i, { requested_value: e.target.value ? parseFloat(e.target.value) : null })} /></Field>
            </div>
            <ChipsField t={t} label={t("editor_project.field_benefits")} values={p.benefits || []} onChange={(v) => update(i, { benefits: v })} />
          </div>
        ))}
      </div>
    </Card>
  );
}

function ScriptUploader({ t, project, onChange }) {
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 30 * 1024 * 1024) return toast.error("Máximo 30MB");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ script_url: apiUrl(data.url), script_filename: file.name });
      toast.success(t("editor_project.script_uploaded"));
    } catch (e) {
      toast.error(t("editor_project.script_failed"));
    }
  };
  return (
    <Field label={t("editor_project.script_field")}>
      <div className="flex items-center gap-3">
        <input ref={ref} type="file" accept="application/pdf" hidden onChange={(e) => upload(e.target.files?.[0])} />
        <Button type="button" variant="outline" onClick={() => ref.current?.click()} data-testid="button-upload-script">
          <Upload size={14} className="mr-2" /> {t("editor_project.script_upload")}
        </Button>
        {project.script_url && (
          <a href={project.script_url} target="_blank" rel="noreferrer" className="text-sm text-petroleum hover:text-gold">
            {project.script_filename || t("editor_project.script_view_default")}
          </a>
        )}
      </div>
      <p className="text-xs text-slate-500 mt-2">{t("editor_project.script_hint")}</p>
    </Field>
  );
}

function cryptoId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, "");
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}
